import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
    CostType,
    CostWeightType,
    CreateCostRequest,
    PalletType,
    ProductCategoryDto,
    ProductDto,
    ProductGroupDto
} from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { createCost, showEditCostDialog, showNewCostDialog } from '../redux/calculationSlices';
import PercentageCostDialogContent from './PercentageCostDialogContent';
import PricePerPackageCostDialogContent from './PricePerPackageCostDialogContent';
import PricePerPalletCostDialogContent from './PricePerPalletCostDialogContent';
import PricePerPriceUnitCostDialogContent from './PricePerPriceUnitCostDialogContent';
import ProductCategoryCostDialogContent from './ProductCategoryCostDialogContent';
import ProductCostDialogContent from './ProductCostDialogContent';
import ProductGroupCostDialogContent from './ProductGroupCostDialogContent';
const mapStateToProps = (state: RootState) => ({
    isCreateCostGroupDialogOpen: state.calculation.showCreateNewCostDialog.showDialog,
    costGroupId: state.calculation.showCreateNewCostDialog.costGroupId,
    costType: state.calculation.showCreateNewCostDialog.costTypeId
});

type Props = ReturnType<typeof mapStateToProps>;

const CreateCostDialog = ({ isCreateCostGroupDialogOpen, costGroupId, costType }: Props) => {
    const dispatch = useDispatch();
    const dispatchHideNewCostDialog = () => {
        dispatch(
            showNewCostDialog({
                costGroupId: undefined,
                costTypeId: undefined,
                showDialog: false
            })
        );
        dispatch(showEditCostDialog(null));
    };

    const getFormInitialState = () => {
        return {
            name: '',
            amount: 0,
            useBasePrice: false,
            selectedProductCategoryId: 0,
            selectedProductCategoryName: '',
            selectedProductGroupId: 0,
            selectedProductGroupName: '',
            selectedProductId: 0,
            selectedProductName: '',
            costWeightType: CostWeightType.GrossWeight,
            palletType: PalletType.EUR
        };
    };

    const [formState, setState] = useState(getFormInitialState());

    const handleUseBasePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState({ ...formState, [event.target.name]: event.target.checked });
    };

    const handleNameChange = (inputValue: string) => {
        setState({ ...formState, name: inputValue });
    };

    const handleAmountChange = (inputValue: number) => {
        setState({ ...formState, amount: inputValue });
    };

    const handleCostWeightTypeChange = (inputValue: CostWeightType) => {
        setState({
            ...formState,
            costWeightType: inputValue
        });
    };
    const handlePalletTypeChange = (inputValue: PalletType) => {
        setState({
            ...formState,
            palletType: inputValue
        });
    };
    const handleProductCategorySelected = (productCategory: ProductCategoryDto | null) => {
        setState({
            ...formState,
            selectedProductCategoryId: productCategory?.productCategoryId ?? 0,
            selectedProductCategoryName: productCategory?.name ?? '',
            name: productCategory?.name ?? ''
        });
    };

    const handleProductGroupSelected = (productGroup: ProductGroupDto | null) => {
        setState({
            ...formState,
            selectedProductGroupId: productGroup?.productGroupId ?? 0,
            selectedProductGroupName: productGroup?.name ?? '',
            name: productGroup?.name ?? ''
        });
    };

    const handleProductSelected = (product: ProductDto | null) => {
        setState({
            ...formState,
            selectedProductId: product?.productId ?? 0,
            selectedProductName: product?.name ?? '',
            name: product?.name ?? ''
        });
    };

    const handleSubmit = () => {
        dispatchCreateNewCost();
    };

    const getCostName = (costType: CostType | undefined) => {
        switch (costType) {
            case CostType.ProductCategoryPercentage:
                return formState.selectedProductCategoryName;
            case CostType.ProductPriceUnit:
            case CostType.ProductPercentage:
                return formState.selectedProductName;
            case CostType.ProductGroupPercentage:
                return formState.selectedProductGroupName;
        }
        return formState.name;
    };

    const dispatchCreateNewCost = () => {
        const costDto: CreateCostRequest = {
            name: getCostName(costType),
            useBasePrice: formState.useBasePrice,
            amount: Number(formState.amount),
            costGroupId: costGroupId!,
            costType: costType!,
            order: 0,
            overridesCostId: undefined,
            productCategoryId: formState.selectedProductCategoryId,
            productGroupId: formState.selectedProductGroupId,
            productId: formState.selectedProductId,
            costWeightType: formState.costWeightType,
            palletType: formState.palletType
        };

        dispatch(
            createCost.request({
                costGroupId: costGroupId!,
                createCostRequest: costDto
            })
        );
        dispatchHideNewCostDialog();
        // reset the form
        setState(getFormInitialState());
    };

    const getDialogContent = () => {
        switch (costType as CostType) {
            case CostType.Percentage:
                return (
                    <PercentageCostDialogContent
                        handleAmountChange={handleAmountChange}
                        handleUseBasePrice={handleUseBasePrice}
                        handleNameChange={handleNameChange}
                        useBasePrice={formState.useBasePrice}
                        name={formState.name}
                        amount={formState.amount}
                    />
                );
            case CostType.PriceUnit:
                return (
                    <PricePerPriceUnitCostDialogContent
                        handleAmountChange={handleAmountChange}
                        handleNameChange={handleNameChange}
                        name={formState.name}
                        amount={formState.amount}
                        costWeightType={formState.costWeightType}
                        handleCostWeightTypeChanged={handleCostWeightTypeChange}
                    />
                );
            case CostType.PerPallet:
                return (
                    <PricePerPalletCostDialogContent
                        handleAmountChange={handleAmountChange}
                        handleNameChange={handleNameChange}
                        handlePalletTypeChange={handlePalletTypeChange}
                        name={formState.name}
                        amount={formState.amount}
                        palletType={formState.palletType}
                    />
                );
            case CostType.PerBox:
                return (
                    <PricePerPackageCostDialogContent
                        handleAmountChange={handleAmountChange}
                        handleNameChange={handleNameChange}
                        handleUseBasePrice={handleUseBasePrice}
                        useBasePrice={formState.useBasePrice}
                        name={formState.name}
                        amount={formState.amount}
                    />
                );
            case CostType.ProductCategoryPercentage:
                return (
                    <ProductCategoryCostDialogContent
                        handleAmountChange={handleAmountChange}
                        handleProductCategorySelected={handleProductCategorySelected}
                        handleUseBasePrice={handleUseBasePrice}
                        useBasePrice={formState.useBasePrice}
                        name={formState.name}
                        amount={formState.amount}
                        productCategoryId={formState.selectedProductCategoryId}
                    />
                );
            case CostType.ProductGroupPercentage:
                return (
                    <ProductGroupCostDialogContent
                        handleAmountChange={handleAmountChange}
                        amount={formState.amount}
                        handleProductGroupChanged={handleProductGroupSelected}
                        handleUseBasePrice={handleUseBasePrice}
                        productGroupId={formState.selectedProductGroupId}
                        useBasePrice={formState.useBasePrice}
                    />
                );
            case CostType.ProductPercentage:
            case CostType.ProductPriceUnit:
                return (
                    <ProductCostDialogContent
                        amount={formState.amount}
                        handleUseBasePrice={handleUseBasePrice}
                        productId={formState.selectedProductId}
                        useBasePrice={formState.useBasePrice}
                        handleAmountChange={handleAmountChange}
                        handleProductChanged={handleProductSelected}
                        costType={costType!}
                        costWeightType={formState.costWeightType}
                        handleCostWeightTypeChanged={handleCostWeightTypeChange}
                    />
                );
        }
    };

    return (
        <Dialog
            onClose={dispatchHideNewCostDialog}
            aria-labelledby="simple-dialog-title"
            open={isCreateCostGroupDialogOpen}
            maxWidth="sm"
            fullWidth={true}
        >
            <DialogTitle id="simple-dialog-title">Create cost</DialogTitle>
            <DialogContent>{getDialogContent()}</DialogContent>
            <DialogActions>
                <Button onClick={dispatchHideNewCostDialog} color="primary">
                    Close
                </Button>
                <Button onClick={handleSubmit} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CreateCostDialog);
